export function Privacy() {
  return (
    <article className="prose prose-sm max-w-3xl mx-auto px-6 py-12 prose-headings:font-semibold">
      <h1>Privacy Policy</h1>
      <p>
        <em>
          Last updated: 2026-05-09. Operator: Eli Stowe, sole trader, Newcastle
          NSW, Australia. Contact:{" "}
          <a href="mailto:privacy@chart-to-csv.com">privacy@chart-to-csv.com</a>.
        </em>
      </p>
      <p>
        We handle personal information in line with the{" "}
        <a
          href="https://www.oaic.gov.au/privacy/australian-privacy-principles"
          target="_blank"
          rel="noopener noreferrer"
        >
          Australian Privacy Principles
        </a>{" "}
        under the <em>Privacy Act 1988</em> (Cth). This page tells you what we
        collect, why, where it goes, how long we keep it, and how to delete it.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Chart images you upload.</strong> Posted to our Cloudflare
          Worker, then forwarded to Anthropic's Messages API for the
          extraction step. Optionally cached for up to 7 days in Cloudflare R2
          to support retry / verification flows; cache entries auto-expire.
        </li>
        <li>
          <strong>Email address</strong> — only if you sign up for the paid
          tier. Used for magic-link sign-in and billing receipts.
        </li>
        <li>
          <strong>Subscription state</strong> — paid / cancelled / past-due,
          with a Stripe customer ID. Stored in our Cloudflare KV for
          authentication.
        </li>
        <li>
          <strong>IP address</strong> — used transiently to enforce the
          free-tier daily limit (5 charts / IP / day). Counters expire after
          24 hours. We do not log raw IPs to long-term storage.
        </li>
        <li>
          <strong>Aggregate analytics</strong> via Plausible — page views and
          conversion events. Plausible is{" "}
          <a
            href="https://plausible.io/data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            cookieless and GDPR-friendly
          </a>{" "}
          and does not track you across sites.
        </li>
      </ul>

      <h2>What we don't collect / do</h2>
      <ul>
        <li>We do not sell, share, or rent personal information.</li>
        <li>
          We do not use your uploads to train Anthropic, our own, or any other
          model. The Anthropic API call is a one-shot inference; no training
          retention applies under{" "}
          <a
            href="https://privacy.anthropic.com/en/articles/8956058-how-do-you-use-personal-data-in-model-training"
            target="_blank"
            rel="noopener noreferrer"
          >
            Anthropic's commercial usage policy
          </a>
          .
        </li>
        <li>
          We do not place tracking cookies. We do use{" "}
          <strong>browser localStorage</strong> to keep your magic-link
          session token after sign-in — that's only readable by this site and
          you can clear it from your browser settings.
        </li>
      </ul>

      <h2>Subprocessors and where data goes</h2>
      <p>
        We use the following third parties to run the service. By using Chart
        to CSV you agree to your data being handled by these processors. Most
        are based in the United States — international data transfer is
        unavoidable for an AI vision service of this shape.
      </p>
      <ul>
        <li>
          <strong>Anthropic</strong> (US) — vision extraction.{" "}
          <a
            href="https://www.anthropic.com/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy policy
          </a>
          .
        </li>
        <li>
          <strong>Cloudflare</strong> (global) — hosting, Workers, KV, R2,
          DNS.{" "}
          <a
            href="https://www.cloudflare.com/privacypolicy/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy policy
          </a>
          .
        </li>
        <li>
          <strong>Stripe</strong> (US, AU subsidiary) — billing.{" "}
          <a
            href="https://stripe.com/au/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy policy
          </a>
          .
        </li>
        <li>
          <strong>Resend</strong> (US) — transactional email delivery.{" "}
          <a
            href="https://resend.com/legal/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy policy
          </a>
          .
        </li>
        <li>
          <strong>Plausible</strong> (EU) — aggregate analytics.{" "}
          <a
            href="https://plausible.io/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy policy
          </a>
          .
        </li>
      </ul>

      <h2>How long we keep things</h2>
      <ul>
        <li>
          <strong>Chart images:</strong> up to 7 days in R2 cache. Auto-expire
          via R2 lifecycle policy.
        </li>
        <li>
          <strong>Magic-link tokens:</strong> 15 minutes, single-use.
        </li>
        <li>
          <strong>Free-tier IP counters:</strong> 24 hours rolling.
        </li>
        <li>
          <strong>Account record</strong> (email, subscription state):
          retained while your subscription is active, plus 7 years for
          Australian tax / financial-record obligations after cancellation.
        </li>
        <li>
          <strong>Stripe billing records:</strong> retained per Stripe's own
          retention policies.
        </li>
      </ul>

      <h2>Your rights</h2>
      <p>Under the Australian Privacy Principles you have the right to:</p>
      <ul>
        <li>
          <strong>Access</strong> the personal information we hold about you.
        </li>
        <li>
          <strong>Correct</strong> information that's inaccurate.
        </li>
        <li>
          <strong>Delete</strong> your account and associated data (subject to
          tax-record retention obligations).
        </li>
        <li>
          <strong>Complain</strong> if you think we've mishandled your data.
        </li>
      </ul>
      <p>
        To exercise any of these, email{" "}
        <a href="mailto:privacy@chart-to-csv.com">privacy@chart-to-csv.com</a>{" "}
        with the subject line "Data request" — we'll respond within 30 days
        (usually 72 hours).
      </p>
      <p>
        If you're unhappy with our response, you can complain to the Office
        of the Australian Information Commissioner:{" "}
        <a
          href="https://www.oaic.gov.au/privacy/privacy-complaints"
          target="_blank"
          rel="noopener noreferrer"
        >
          oaic.gov.au/privacy/privacy-complaints
        </a>
        .
      </p>

      <h2>Children</h2>
      <p>
        Chart to CSV is not directed at children under 16. We do not knowingly
        collect information from children. If you believe a child has signed
        up, email us and we'll delete the account.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We'll update the "Last updated" date above when this policy changes.
        For material changes (new subprocessor, new data category collected,
        new retention period), paid users will be notified by email at least
        30 days before the change takes effect.
      </p>
    </article>
  );
}
